import { useState, useCallback, forwardRef, type SyntheticEvent } from 'react';
import { useVideoControl } from '@/hooks/useVideoControl';
import { PlayCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { Preloader } from '../Preloader';
import { VideoPlayerSlider } from './VideoPlayerSlider.tsx';
import { useMeasure } from '@uidotdev/usehooks';

interface VideoPlayerProps {
  src?: string;
  onSetCurRef?: (ref: HTMLVideoElement | null) => void;
  autoPlay?: boolean;
  style?: React.CSSProperties;
  PlayIcon?: React.ReactNode;
  ReplayIcon?: React.ReactNode;
  width?: string;
  height?: string;
  type?: 'video/webm' | 'video/mp4';
  controls?: boolean;
  blurred?: boolean;
  children?: React.ReactNode;
}

export const VideoPlayer = forwardRef<HTMLVideoElement, VideoPlayerProps>(
  (
    {
      src,
      onSetCurRef,
      autoPlay,
      style,
      PlayIcon,
      ReplayIcon,
      width = '330px',
      height, // optional override
      controls,
      children,
    },
    ref
  ) => {
    const { isPlaying, play, setCurRef, isReady, curTime, duration, curRef } =
      useVideoControl();
    const [wrapperRef, wrapperSize] = useMeasure();

    const [rotate, setRotate] = useState(false);
    const [aspectRatioCSS, setAspectRatioCSS] = useState<number[] | undefined>(
      []
    );

    const handleLoadedMetadata = useCallback(
      (e: SyntheticEvent<HTMLVideoElement>) => {
        const video = e.currentTarget;
        // Safari preload hack
        if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
          video.currentTime = 0.1;
        }
        // Decide if we need to rotate
        const shouldRotate = video.videoWidth > video.videoHeight;
        setRotate(shouldRotate);

        // Compute CSS aspect-ratio string:
        const ratio = shouldRotate
          ? [video.videoHeight, video.videoWidth]
          : [video.videoWidth, video.videoHeight];
        setAspectRatioCSS(ratio);
      },
      []
    );

    // Build wrapper style
    const wrapperStyle: React.CSSProperties = {
      width,
      overflow: 'hidden',
      position: 'relative',
      ...style,
    };
    // If they passed an explicit height prop, honor it.
    if (height) {
      wrapperStyle.height = height;
    } else if (aspectRatioCSS) {
      // Otherwise, auto-height by aspect-ratio
      // (modern browsers support this)
      wrapperStyle.aspectRatio = aspectRatioCSS.join(' / ');
      wrapperStyle.paddingTop =
        Math.floor((aspectRatioCSS[1] / aspectRatioCSS[0]) * 100) + '%';
    }

    // Video itself: center, cover, and rotate if needed
    const videoStyle: React.CSSProperties = {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transformOrigin: 'center center',
      objectFit: 'cover',
      maxWidth: 'unset',
      transform: `translate(-50%, -50%)${rotate ? ' rotate(90deg)' : ''}`,
    };

    if (rotate) {
      [, videoStyle.height] = [
        wrapperSize.height + 'px',
        wrapperSize.width + 'px',
      ];
    } else {
      videoStyle.maxWidth = '100%';
    }

    return (
      <div style={wrapperStyle} ref={wrapperRef}>
        <video
          ref={(node) => {
            setCurRef(node);
            onSetCurRef?.(node);
            if (typeof ref === 'function') ref(node);
            else if (ref) ref.current = node;
          }}
          src={src}
          preload="metadata"
          playsInline
          autoPlay={autoPlay}
          onClick={play}
          onLoadedMetadata={handleLoadedMetadata}
          style={videoStyle}
        >
          {children}
        </video>

        {!isReady && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Preloader />
          </div>
        )}

        {controls && (
          <div className="flex w-full absolute bottom-0 z-20">
            <VideoPlayerSlider
              currentTime={curTime}
              duration={duration}
              onTimeChange={(time) => {
                if (curRef) curRef.currentTime = time;
              }}
            />
          </div>
        )}

        <div
          style={{
            pointerEvents: 'none',
            position: 'absolute',
            inset: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 3,
          }}
        >
          {!isPlaying &&
            isReady &&
            curTime < duration &&
            (PlayIcon || (
              <PlayCircleOutlined
                style={{ fontSize: 40, color: 'white' }}
                className="animate-pulse-size"
              />
            ))}
          {!isPlaying &&
            isReady &&
            curTime >= duration &&
            (ReplayIcon || (
              <ReloadOutlined style={{ fontSize: 40, color: 'white' }} />
            ))}
        </div>
      </div>
    );
  }
);
